import { parseAmountToMinorUnits } from 'features/feeBasedInstantTransfer/helpers/transferAmount';
import type { TransferRoutePolicy } from 'features/feeBasedInstantTransfer/hooks/useSelectedRoutePolicy';

export type TransferAmountError =
  'limitBlocked' | 'belowMinimum' | 'aboveMaximum' | 'transferNotPossible';

export interface TransferAmountEvaluation {
  isValid: boolean;
  error?: TransferAmountError;
}

interface EvaluateTransferAmountParams {
  amount: string;
  policy: TransferRoutePolicy;
}

const INVALID: TransferAmountEvaluation = { isValid: false };

type TranslateLimitMessage = (
  key: 'amountBelowMinimum' | 'amountExceedsTransactionLimit',
  options: { limit?: string },
) => string;

/** The caption shown for an amount error: the server message, or a local fallback for limits. */
export const getTransferAmountErrorText = (
  error: TransferAmountError | undefined,
  policy: TransferRoutePolicy,
  translatedText: TranslateLimitMessage,
) => {
  if (error === 'transferNotPossible') {
    return policy.status === 'unavailable' ? policy.message : undefined;
  }
  if (!error || policy.status !== 'ready') {
    return undefined;
  }
  if (error === 'limitBlocked') {
    return policy.limitErrorMessage;
  }
  if (error === 'belowMinimum') {
    return (
      policy.minimumMessage ??
      translatedText('amountBelowMinimum', {
        limit: policy.minimumPrincipal?.formatted,
      })
    );
  }
  return (
    policy.maximumMessage ??
    translatedText('amountExceedsTransactionLimit', {
      limit: policy.maximumPrincipal?.formatted,
    })
  );
};

/**
 * Evaluates a "X.XX" transfer amount against the selected route policy.
 * At most one error is returned, in priority order: route unavailable, limit
 * blocked, below minimum, above maximum. Limits come from the policy as-is
 * and are skipped when absent; no fee or total is calculated on the client.
 */
export const evaluateTransferAmount = ({
  amount,
  policy,
}: EvaluateTransferAmountParams): TransferAmountEvaluation => {
  const amountMinorUnits = parseAmountToMinorUnits(amount);
  if (amountMinorUnits === undefined) {
    return INVALID;
  }

  if (policy.status === 'idle' || policy.status === 'loading') {
    return INVALID;
  }

  if (policy.status === 'unavailable') {
    return policy.message
      ? { isValid: false, error: 'transferNotPossible' }
      : INVALID;
  }

  if (policy.limitErrorMessage) {
    return { isValid: false, error: 'limitBlocked' };
  }

  if (amountMinorUnits === 0) {
    return INVALID;
  }

  const { minimumPrincipal, maximumPrincipal } = policy;
  if (minimumPrincipal && amountMinorUnits < minimumPrincipal.minorUnits) {
    return { isValid: false, error: 'belowMinimum' };
  }

  if (maximumPrincipal && amountMinorUnits > maximumPrincipal.minorUnits) {
    return { isValid: false, error: 'aboveMaximum' };
  }

  return { isValid: true };
};
